import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Plus, Search, AlertTriangle, ArrowRight, Loader2, Send } from "lucide-react";

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");
const EMPTY = { prodae: "", nominativo: "", indirizzo: "", email: "", telefono: "", prezzo_blsd: "", prezzo_agg_blsd: "" };
export const clientToForm = (c) => ({ prodae: c.prodae || "", nominativo: c.nominativo || "", indirizzo: c.indirizzo || "", email: c.email || "", telefono: c.telefono || "", prezzo_blsd: c.prezzo_blsd ?? "", prezzo_agg_blsd: c.prezzo_agg_blsd ?? "" });
export const clientPayload = (f) => ({ ...f, prezzo_blsd: f.prezzo_blsd === "" ? null : Number(f.prezzo_blsd), prezzo_agg_blsd: f.prezzo_agg_blsd === "" ? null : Number(f.prezzo_agg_blsd) });
export const eur = (v) => (v == null || v === "" ? "—" : `€ ${Number(v).toFixed(2)}`);

export const ClientForm = ({ form, setForm }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {[["prodae", "N° PRODAE"], ["nominativo", "Ragione Sociale / Nominativo *"], ["indirizzo", "Indirizzo"], ["email", "Mail"], ["telefono", "Telefono"]].map(([k, l]) => (
      <div key={k} className={k === "indirizzo" ? "sm:col-span-2" : ""}>
        <Label className="nx-label">{l}</Label>
        <Input data-testid={`formazione-client-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" />
      </div>
    ))}
    {[["prezzo_blsd", "Prezzo BLSD (€)"], ["prezzo_agg_blsd", "Prezzo Agg. BLSD (€)"]].map(([k, l]) => (
      <div key={k}>
        <Label className="nx-label">{l}</Label>
        <Input data-testid={`formazione-client-${k}`} type="number" step="0.01" min="0" placeholder="0.00" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" />
      </div>
    ))}
  </div>
);

export default function FormazionePage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/formazione/clienti").then((r) => setClients(r.data)).catch(() => setClients([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/formazione/clienti", clientPayload(form));
      toast.success("Cliente creato"); setOpen(false); setForm(EMPTY);
      navigate(`/operativa/formazione/${data.id}`);
    } catch (err) { toast.error("Non creato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSaving(false); }
  };

  const sendAlerts = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/formazione/alerts/invia");
      toast.success(data.inviati ? `Alert inviato per ${data.inviati} persone` : "Nessuna nuova scadenza da notificare");
    } catch { toast.error("Invio non riuscito"); } finally { setSending(false); }
  };

  const list = (clients || []).filter((c) => !q || c.nominativo.toLowerCase().includes(q.toLowerCase()) || (c.prodae || "").toLowerCase().includes(q.toLowerCase()));
  const totAlert = (clients || []).filter((c) => c.n_in_scadenza > 0).length;

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto nx-fade-in pb-10">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2"><GraduationCap className="h-7 w-7 text-primary" /> Formazione</h1>
            <p className="text-sm text-slate-500">Clienti formati, persone certificate e scadenze dei cicli formativi.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="formazione-send-alerts" variant="outline" className="gap-2" onClick={sendAlerts} disabled={sending}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia alert scadenze
            </Button>
            <Button data-testid="formazione-new-client" className="gap-2" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Nuovo cliente</Button>
          </div>
        </div>

        {totAlert > 0 && (
          <div data-testid="formazione-alert-banner" className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4" /> {totAlert} {totAlert === 1 ? "cliente ha" : "clienti hanno"} persone con corso in scadenza entro 3 mesi.
          </div>
        )}

        <Card className="border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="formazione-search" placeholder="Cerca cliente o PRODAE..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" />
            </div>
            <span className="ml-auto text-xs text-slate-400">{clients ? `${clients.length} clienti` : ""}</span>
          </div>
          {clients === null ? (
            <div className="p-5 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-10 w-full" />)}</div>
          ) : list.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">Nessun cliente formato. Aggiungi il primo cliente.</div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-14">N°</TableHead><TableHead>Nominativo</TableHead><TableHead>PRODAE</TableHead>
                <TableHead className="text-center">Certificati</TableHead><TableHead>Ultima attività</TableHead><TableHead>Scadenze</TableHead><TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {list.map((c) => (
                  <TableRow key={c.id} data-testid={`formazione-row-${c.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/operativa/formazione/${c.id}`)}>
                    <TableCell className="font-mono text-xs text-slate-500">{String(c.progressivo).padStart(3, "0")}</TableCell>
                    <TableCell className="font-semibold text-slate-800">{c.nominativo}</TableCell>
                    <TableCell className="text-slate-600">{c.prodae || "—"}</TableCell>
                    <TableCell className="text-center font-semibold">{c.n_certificati}</TableCell>
                    <TableCell className="text-slate-600">{fmtDate(c.ultima_attivita)}</TableCell>
                    <TableCell>
                      {c.n_in_scadenza > 0 ? (
                        <span data-testid={`formazione-alert-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-700 px-2.5 py-0.5 text-xs font-semibold">
                          <AlertTriangle className="h-3.5 w-3.5" /> {c.n_in_scadenza} in scadenza
                        </span>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right"><ArrowRight className="h-4 w-4 text-slate-400 inline" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Nuovo cliente formato</DialogTitle><DialogDescription>Inserisci i dati anagrafici del cliente.</DialogDescription></DialogHeader>
            <ClientForm form={form} setForm={setForm} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="formazione-client-save" onClick={create} disabled={saving || !form.nominativo.trim()}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
